// src/layout/OrgSwitcher.jsx
//
// Topbar account switcher (labeled "Account" in the UI; internally still
// org/tenant — see shellConfig.js). Reads/writes the shared WorkspaceContext
// (mounted at the app root in main.jsx) — the same source engagement
// scoping, module gating, and permission checks already use, so selecting
// an entry here actually scopes the rest of the app.
//
// Registering a new Account is Admin > Accounts' job (see
// accounts/pages/AccountsPage.jsx) — this switcher only selects among
// Accounts that already exist.

import PropTypes from "prop-types";
import { ChevronsUpDown, Check, RefreshCw } from "lucide-react";
import { useWorkspace } from "../context/WorkspaceContext";
import { Skeleton } from "../shared/ui";

export default function OrgSwitcher({ open, onToggle }) {
  const { accounts, loading, error, currentAccount, selectAccount, refresh } = useWorkspace();

  const label = currentAccount?.name || (loading ? "Loading…" : "Select account");
  const initials = currentAccount?.name?.slice(0, 2).toUpperCase() || "··";

  return (
    <div style={{ position: "relative" }}>
      <button type="button" className="orion-pill-btn" onClick={onToggle}>
        <span className="orion-mark22">{initials}</span>
        <span className="orion-pill-label">{label}</span>
        <span className="orion-chevud"><ChevronsUpDown size={14} /></span>
      </button>

      {open && (
        <div className="orion-dropmenu" style={{ width: 280 }}>
          <div className="orion-drop-hd">Account</div>

          {error ? (
            <button type="button" className="orion-drop-row" onClick={() => refresh()}>
              <RefreshCw size={14} />
              <div className="meta"><div className="t">Couldn't load accounts — retry</div></div>
            </button>
          ) : loading && accounts.length === 0 ? (
            <div className="orion-drop-row disabled">
              <div className="meta flex flex-col gap-1.5">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ) : (
            accounts.map((account) => (
              <button
                key={account.account_id}
                type="button"
                className={`orion-drop-row${account.account_id === currentAccount?.account_id ? " on" : ""}`}
                onClick={() => {
                  selectAccount(account.account_id);
                  onToggle();
                }}
              >
                <span className="orion-mark22">{account.name.slice(0, 2).toUpperCase()}</span>
                <div className="meta">
                  <div className="t">{account.name}</div>
                </div>
                {account.account_id === currentAccount?.account_id && (
                  <span className="check"><Check size={16} /></span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
OrgSwitcher.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
